import { useParams, Link, Navigate } from 'react-router-dom';
import { getArticleBySlug, getRelatedArticles, getFeaturedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const related = getRelatedArticles(article);
  const categoryInfo = categories.find((c) => c.slug === article.category);
  const featuredArticles = getFeaturedArticles().filter((a) => a.slug !== article.slug).slice(0, 3);

  const displayRelated = related.length >= 2 ? related : featuredArticles;

  return (
    <div className="min-h-screen pt-16">
      {/* Article Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            image: article.coverImage,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.publishDate,
            publisher: {
              '@type': 'Organization',
              name: '17c.c',
              url: 'https://17c.c',
            },
          }),
        }}
      />

      {/* Header */}
      <section className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-4 md:px-6 pb-8 md:pb-12">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: categoryInfo?.name || article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-10 md:py-14 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-xl md:text-3xl font-bold text-foreground-900 font-heading">
            {article.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-user-line" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {article.readTime}
            </span>
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors whitespace-nowrap cursor-pointer"
            >
              {article.categoryName}
            </Link>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-2.5 py-1 rounded-md bg-background-100 text-foreground-500 text-xs"
              >
                #{tag}
              </span>
            ))}
          </div>

          <div
            className="mt-8 prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
              [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:font-heading [&_h2]:mt-8 [&_h2]:mb-4
              [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:text-foreground-800 [&_h3]:font-heading [&_h3]:mt-6 [&_h3]:mb-3
              [&_p]:text-foreground-600 [&_p]:leading-relaxed [&_p]:mb-4
              [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_ul]:text-foreground-600 [&_ul]:space-y-1
              [&_li]:leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </article>

      {/* Related Articles */}
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading mb-8">
            相关推荐
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {displayRelated.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}